import type { Comparator, Entity } from "~/shared/types";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

const makeKey = (key: string, extra: string | null) => JSON.stringify([key, extra ?? null]);
const extractKeyAndExtra = (key: string) => {
  const value = JSON.parse(key);
  return { key: value[0], extra: value[1] ?? null };
};

export type PaginationState = {
  duids: Set<string>;
  lastEntity: Entity | null;
  loadedAll: boolean;
  comparator: Comparator<Entity | undefined>;
  validator?: (entity: Entity) => boolean;
};

export type State = {
  _keyToPaginationState: Map<string, PaginationState>;
};

export const state: State = {
  _keyToPaginationState: new Map(),
};

export type Getters = {
  $getPaginationState: (key: string, extra: string | null) => PaginationState | undefined;
};

export type Actions = {
  /** Create a pagination state.
   * @PRIVATE */
  $createPaginationState: (
    key: string,
    extra: string | null,
    comparator: Comparator,
    validator?: (entity: Entity) => boolean
  ) => PaginationState;
  /** Clear a pagination state.
   * @PRIVATE */
  $clearPaginationState: (key: string, extra: string | null) => void;
  /** Add entities to pagination state.
   * @PRIVATE */
  $addEntitiesToPaginationState: (
    key: string,
    extra: string | null,
    entities: Entity[],
    totalCount: number | null
  ) => void;
  /** Add entities to all related pagination states.
   * @PRIVATE */
  $addEntitiesToPaginationStates: (key: string, entities: Entity[]) => void;
  /** Remove entities from all pagination states.
   * @PRIVATE */
  $removeEntitiesFromPaginationStates: (entities: Entity[]) => void;
  /** Remove an entity from a specific pagination state.
   * @PRIVATE */
  $removeEntityFromPaginationState: (key: string, extra: string | null, entity: Entity) => void;
  /** Delete all related pagination states.
   * @PRIVATE */
  $deletePaginationStates: (key: string) => void;
  /** Remove an entity from pagination states if it doesn't pass the validator.
   * @PRIVATE */
  $maybeRemoveInvalidEntityFromPaginationStates: (entity: Entity) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  $getPaginationState() {
    return (key, extra) => this._keyToPaginationState.get(makeKey(key, extra));
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  $createPaginationState(key, extra, comparator, validator) {
    const paginationState: PaginationState = {
      duids: new Set(),
      lastEntity: null,
      loadedAll: false,
      comparator,
      validator,
    };
    this._keyToPaginationState.set(makeKey(key, extra), paginationState);
    return paginationState;
  },
  $clearPaginationState(key, extra) {
    const paginationState = this.$getPaginationState(key, extra);
    if (!paginationState) {
      return;
    }

    paginationState.duids.clear();
    paginationState.lastEntity = null;
    paginationState.loadedAll = false;
  },
  $addEntitiesToPaginationState(key, extra, entities, totalCount) {
    const paginationState = this.$getPaginationState(key, extra);
    if (!paginationState) {
      return;
    }

    const validEntities = paginationState.validator ? entities.filter(paginationState.validator) : entities;

    // Add entities if there's no last entity or if the entities are same or before the last entity
    // Otherwise, remove the entity from the duids
    let lastAddedEntity: Entity | null = null;
    validEntities.forEach((entity) => {
      if (
        paginationState.loadedAll ||
        !paginationState.lastEntity ||
        // TODO incremental, remove totalCount check and fix comparators when loading paginated pages
        totalCount !== null ||
        paginationState.comparator(entity, paginationState.lastEntity) <= 0
      ) {
        paginationState.duids.add(entity.duid);
        lastAddedEntity = entity;
      } else if (totalCount === null) {
        paginationState.duids.delete(entity.duid);
      }
    });

    if (totalCount !== null) {
      paginationState.loadedAll = paginationState.duids.size >= totalCount;

      if (!paginationState.lastEntity) {
        paginationState.lastEntity = lastAddedEntity;
      } else if (lastAddedEntity && paginationState.comparator(lastAddedEntity, paginationState.lastEntity) >= 0) {
        paginationState.lastEntity = lastAddedEntity;
      }
    }
  },
  $addEntitiesToPaginationStates(key, entities) {
    [...this._keyToPaginationState.entries()].forEach(([fullKey]) => {
      const { key: extractedKey, extra } = extractKeyAndExtra(fullKey);
      if (extractedKey === key) {
        this.$addEntitiesToPaginationState(extractedKey, extra, entities, null);
      }
    });
  },
  $maybeRemoveInvalidEntityFromPaginationStates(entity) {
    [...this._keyToPaginationState.entries()].forEach(([fullKey, paginationState]) => {
      if (!paginationState.validator || !paginationState.duids.has(entity.duid)) {
        return;
      }

      if (!paginationState.validator(entity)) {
        const { key: extractedKey, extra } = extractKeyAndExtra(fullKey);
        this.$removeEntityFromPaginationState(extractedKey, extra, entity);
      }
    });
  },
  $removeEntitiesFromPaginationStates(entities) {
    [...this._keyToPaginationState.entries()].forEach(([, paginationState]) => {
      entities.forEach((entity) => {
        paginationState.duids.delete(entity.duid);
        if (entity.duid === paginationState.lastEntity?.duid) {
          // eslint-disable-next-line no-param-reassign
          paginationState.lastEntity = null;
        }
      });
    });
  },
  $removeEntityFromPaginationState(key, extra, entity) {
    const paginationState = this.$getPaginationState(key, extra);
    if (!paginationState) {
      return;
    }

    paginationState.duids.delete(entity.duid);
    if (entity.duid === paginationState.lastEntity?.duid) {
      paginationState.lastEntity = null;
    }
  },
  $deletePaginationStates(key) {
    [...this._keyToPaginationState.entries()].forEach(([paginationKey]) => {
      if (extractKeyAndExtra(paginationKey).key === key) {
        this._keyToPaginationState.delete(paginationKey);
      }
    });
  },
};

export { actions, getters };
